<template>
  <!-- Time Tracking as the app draws it today (Workspace369 origin/main, Oct 22 2026), at the app's own
       scale. Wide containers get the desktop shell (SidebarV2 with Work open, the canvas header) around
       lib/view/time_tracking/time_entries_screen.dart: its 56px header (title, underline tabs, Log time,
       ⋯), the timer bar (widgets/timer_widget.dart: running row and idle row, TtPill from
       widgets/tt_controls.dart), the metrics strip (_buildMetrics, AppMetricStrip), the filter row and the
       entries table grouped by day (widgets/time_entries_table.dart: columns, day rows, timeEntryStatus,
       "09:00–10:30" times, "—" while running). Below 600px it is the phone layout (ResponsiveBuilder
       mobile): the running timer leaves the page for the dark strip above the app (_RunningTimerBanner),
       the tabs row, two metrics, the day list (_PhoneDayHeader, _PhoneEntryRow), the Log time button
       and a bottom bar with Time Tracking pinned as a tab (bottom_nav_catalog.dart). Labels are
       app_en.arb strings; Northstar Studio, its team and clients are sample data (the same clients as the
       Home and Invoices heroes). States: `running` (Maya's timer on Logo concepts) and `saved` (stopped:
       the same row turns into a 1:30 Unbilled entry worth $180, and the week, billable and unbilled
       figures count it). The server renders `saved`. Radii follow the site's rules rather than the app's
       fully rounded status pills and 24px Log time button: tags 4px, pills and buttons 6px, cards 8–10px. -->
  <div class="ttw" :class="`ttw--${state}`" aria-hidden="true">
    <svg class="ttw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`ttw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="ttw-desk">
      <aside class="ttw-side">
        <div class="ttw-side-top"><svg class="ic"><use href="#ttw-view_sidebar" /></svg></div>
        <div class="ttw-ws"><span class="ttw-logo">N</span><span class="ttw-ell">Northstar Studio</span><svg class="ic sm"><use href="#ttw-unfold_more" /></svg></div>
        <nav class="ttw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="ttw-ni top">
              <svg class="ic"><use :href="`#ttw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm ttw-chev"><use :href="`#ttw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="ttw-ni child" :class="{ sel: c === 'Time Tracking' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="ttw-side-foot">
          <div v-for="f in foot" :key="f.k" class="ttw-ni"><svg class="ic"><use :href="`#ttw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="ttw-main">
        <header class="ttw-head">
          <span class="ttw-grow" />
          <span class="ttw-hsearch"><svg class="ic sm"><use href="#ttw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="ttw-icbtn ttw-bell"><svg class="ic"><use href="#ttw-notifications" /></svg><b>3</b></span>
          <span class="ttw-icbtn"><svg class="ic"><use href="#ttw-settings" /></svg></span>
          <span class="ttw-icbtn"><svg class="ic"><use href="#ttw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="ttw-sheet">
          <div class="ttw-top">
            <span class="ttw-title"><svg class="ic sm"><use href="#ttw-schedule" /></svg>Time Tracking</span>
            <span class="ttw-tabs">
              <span class="on">Entries</span><span>Timesheet</span><span>Time off</span><span>Approvals<b>2</b></span><span>Reports</span>
            </span>
            <span class="ttw-grow" />
            <span class="ttw-btn sec"><svg class="ic sm"><use href="#ttw-add" /></svg>Log time</span>
            <span class="ttw-icbtn"><svg class="ic"><use href="#ttw-more_horiz" /></svg></span>
          </div>

          <div class="ttw-body">
            <!-- The timer bar: the running row, then the idle row once it is saved (same 52px height). -->
            <div class="ttw-timer">
              <template v-if="running">
                <span class="ttw-dot live"><i /></span>
                <span class="ttw-desc">Logo concepts</span>
                <span class="ttw-pill set"><svg class="ic xs"><use href="#ttw-folder" /></svg>Brand identity<em> · Marlowe Coffee Co.</em></span>
                <span class="ttw-pill bill">$ Billable</span>
                <b class="ttw-elapsed">1:29:52</b>
                <span class="ttw-sq"><svg class="ic"><use href="#ttw-pause" /></svg></span>
                <span class="ttw-sq fill"><svg class="ic"><use href="#ttw-stop" /></svg></span>
              </template>
              <template v-else>
                <span class="ttw-dot"><i /></span>
                <span class="ttw-desc hint">What are you working on?</span>
                <span class="ttw-pill set"><span class="ttw-av sm">M</span>Me</span>
                <span class="ttw-pill"><svg class="ic xs"><use href="#ttw-folder" /></svg>Project</span>
                <span class="ttw-pill bill">$ Billable</span>
                <span class="ttw-btn"><svg class="ic sm"><use href="#ttw-play_arrow" /></svg>Start</span>
              </template>
            </div>

            <div class="ttw-metrics">
              <div v-for="m in metrics" :key="m.l" class="ttw-m" :class="m.cls">
                <small>{{ m.l }}</small>
                <b :key="`m-${m.v}`" class="ttw-pop">{{ m.v }}</b>
                <span :key="`c-${m.c}`">{{ m.c }}</span>
              </div>
            </div>

            <div class="ttw-filters">
              <span class="ttw-chip on"><svg class="ic xs"><use href="#ttw-calendar_today" /></svg>Date: <b>This week</b></span>
              <span class="ttw-chip"><svg class="ic xs"><use href="#ttw-person_outline" /></svg>Member</span>
              <span class="ttw-chip"><svg class="ic xs"><use href="#ttw-business" /></svg>Client</span>
              <span class="ttw-chip"><svg class="ic xs"><use href="#ttw-folder" /></svg>Project</span>
              <span class="ttw-chip"><svg class="ic xs"><use href="#ttw-receipt_long" /></svg>Billing</span>
              <span class="ttw-chip ttw-comp"><svg class="ic xs"><use href="#ttw-payments" /></svg>Compensation</span>
              <span class="ttw-grow" />
              <span class="ttw-chip"><svg class="ic xs"><use href="#ttw-view_agenda" /></svg>Group: <b>Day</b></span>
            </div>

            <table class="ttw-table">
              <colgroup><col class="w-ck"><col class="w-desc"><col class="w-proj"><col class="w-mem"><col class="w-time"><col class="w-dur"><col class="w-amt"><col class="w-st"><col class="w-more"></colgroup>
              <thead>
                <tr><th><i class="ttw-ck" /></th><th>Description</th><th>Project</th><th class="c-mem">Member</th><th class="c-time">Time</th><th class="r">Duration</th><th class="r">Amount</th><th class="c-st">Status</th><th /></tr>
              </thead>
              <tbody>
                <template v-for="d in days" :key="d.label">
                  <tr class="ttw-day">
                    <td />
                    <td>{{ d.label }}</td>
                    <td />
                    <td class="c-mem" />
                    <td class="c-time" />
                    <td class="r" :key="`dt-${d.total}`">{{ d.total }}</td>
                    <td class="r">{{ d.money }}</td>
                    <td class="c-st" />
                    <td />
                  </tr>
                  <tr v-for="e in d.rows" :key="e.focus ? `${e.k}-${state}` : e.k" :class="{ 'is-focus': e.focus }">
                    <td><i class="ttw-ck" /></td>
                    <td class="ttw-ell">{{ e.desc }}</td>
                    <td><span class="ttw-proj"><b class="ttw-ell">{{ e.proj }}</b><span class="ttw-ell">{{ e.client }}</span></span></td>
                    <td class="c-mem"><span class="ttw-av">{{ e.who }}</span></td>
                    <td class="c-time muted">{{ e.time }}</td>
                    <td class="r strong" :key="`d-${e.k}-${e.dur}`">{{ e.dur }}</td>
                    <td class="r" :class="{ quiet: e.amt === '—' }">{{ e.amt }}</td>
                    <td class="c-st"><em class="ttw-tag" :class="e.tone"><svg v-if="e.st === 'Billed'" class="ic xs"><use href="#ttw-receipt_long" /></svg><i v-else />{{ e.st }}</em></td>
                    <td class="ttw-more"><svg class="ic sm"><use href="#ttw-more_horiz" /></svg></td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="ttw-phone">
      <!-- The running strip sits above the app; once saved, the idle timer card is back in the page. The
           card keeps its slot in both states and the page moves by transform only, so no layout shifts. -->
      <div class="ttp-strip" :class="{ off: !running }">
        <span class="ttp-sdot" />
        <span class="ttp-stxt"><b>Logo concepts</b><small>Brand identity · Billable</small></span>
        <b class="ttp-sel">1:29:52</b>
        <span class="ttp-ssq"><svg class="ic"><use href="#ttw-pause" /></svg></span>
        <span class="ttp-ssq fill"><svg class="ic"><use href="#ttw-stop" /></svg></span>
      </div>
      <div class="ttp-tabs">
        <span class="on">Entries</span><span>Timesheet</span><span>Time off</span><span>Approvals</span><span>Reports</span>
        <span class="ttp-tmore"><svg class="ic"><use href="#ttw-more_horiz" /></svg></span>
      </div>
      <div class="ttp-body">
        <div class="ttp-timer">
          <div class="ttp-trow"><span class="hint">What are you working on?</span><span class="ttw-btn"><svg class="ic"><use href="#ttw-play_arrow" /></svg>Start</span></div>
          <div class="ttp-pills">
            <span class="ttw-pill set"><span class="ttw-av sm">M</span>Me</span>
            <span class="ttw-pill"><svg class="ic"><use href="#ttw-folder" /></svg>Project</span>
            <span class="ttw-pill bill">$ Billable</span>
          </div>
        </div>
        <div class="ttp-metrics">
          <div v-for="m in metrics.filter((x) => x.phone)" :key="m.l" class="ttw-m">
            <small>{{ m.l }}</small>
            <b :key="`pm-${m.v}`" class="ttw-pop">{{ m.v }}</b>
            <span>{{ m.c }}</span>
          </div>
        </div>
        <div class="ttp-filters">
          <span class="ttw-chip on"><svg class="ic"><use href="#ttw-calendar_today" /></svg>Date: <b>This week</b></span>
          <span class="ttw-chip"><svg class="ic"><use href="#ttw-person_outline" /></svg>Member</span>
          <span class="ttw-chip"><svg class="ic"><use href="#ttw-business" /></svg>Client</span>
        </div>
        <template v-for="d in days.slice(0, 1)" :key="`p-${d.label}`">
          <div class="ttp-day"><b>{{ d.label }}</b><span :key="`pdt-${d.total}`">{{ d.total }}</span></div>
          <div v-for="e in d.rows" :key="e.focus ? `p-${e.k}-${state}` : `p-${e.k}`" class="ttp-row" :class="{ 'is-focus': e.focus }">
            <span class="ttp-l"><b class="ttw-ell">{{ e.desc }}</b><span class="ttw-ell">{{ e.proj }} · {{ e.client }}</span></span>
            <span class="ttp-r"><b :key="`pd-${e.k}-${e.dur}`">{{ e.dur }}</b><em class="ttw-tag" :class="e.tone"><svg v-if="e.st === 'Billed'" class="ic"><use href="#ttw-receipt_long" /></svg><i v-else />{{ e.st }}</em></span>
          </div>
        </template>
      </div>
      <span class="ttp-fab"><svg class="ic"><use href="#ttw-add" /></svg>Log time</span>
      <nav class="ttp-nav">
        <span v-for="t in tabs" :key="t.k" :class="{ on: t.on }"><svg class="ic"><use :href="`#ttw-${t.i}`" /></svg><small>{{ t.k }}</small></span>
        <span class="ttp-navmore"><svg class="ic"><use href="#ttw-add" /></svg></span>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { timeIcons } from "~/data/app-icons-time.js";

const props = defineProps({
  // running | saved
  state: { type: String, default: "saved" },
});

const usedApp = ["account_balance_wallet", "add", "auto_awesome", "calendar_today", "chat_bubble_outline", "chevron_right", "dashboard", "expand_more", "forum", "handyman", "logout", "more_horiz", "notifications", "payments", "people_alt", "receipt_long", "search", "settings", "support_agent", "unfold_more", "view_sidebar", "work_outline", "workspaces"];
const icons = { ...Object.fromEntries(usedApp.map((k) => [k, appIcons[k]])), ...timeIcons };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", open: true, kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const tabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "Time Tracking", i: "timer", on: true },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];

const running = computed(() => props.state === "running");

// The team's week before and after Maya saves 1:30 of billable time at $120/h.
const metrics = computed(() => {
  const r = running.value;
  return [
    { l: "This week", v: r ? "61h 20m" : "62h 50m", c: "of 120h capacity", phone: true },
    { l: "Billable", v: r ? "48h 30m" : "50h", c: r ? "79% of tracked time" : "80% of tracked time", cls: "t-hide" },
    { l: "Unbilled", v: r ? "$2,760.00" : "$2,940.00", c: r ? "15 entries ready to bill" : "16 entries ready to bill", phone: true },
    { l: "Billed this month", v: "$8,640.00", c: "41 entries", cls: "t-hide2" },
  ];
});

const days = computed(() => {
  const r = running.value;
  return [
    {
      label: "Today", total: r ? "2h 15m" : "3h 45m", money: r ? "$270.00" : "$450.00",
      rows: [
        r
          ? { k: "logo", desc: "Logo concepts", proj: "Brand identity", client: "Marlowe Coffee Co.", who: "M", time: "08:10–", dur: "—", amt: "—", st: "Running", tone: "green", focus: true }
          : { k: "logo", desc: "Logo concepts", proj: "Brand identity", client: "Marlowe Coffee Co.", who: "M", time: "08:10–09:40", dur: "1:30", amt: "$180.00", st: "Unbilled", tone: "amber", focus: true },
        { k: "copy", desc: "Appointment page copy", proj: "Website refresh", client: "Orchard Veterinary", who: "P", time: "08:00–09:30", dur: "1:30", amt: "$180.00", st: "Unbilled", tone: "amber" },
        { k: "call", desc: "Menu launch call", proj: "Menu relaunch", client: "Juniper Bakery", who: "J", time: "08:30–09:15", dur: "0:45", amt: "$90.00", st: "Unbilled", tone: "amber" },
      ],
    },
    {
      label: "Yesterday", total: "7h 20m", money: "$760.00",
      rows: [
        { k: "guide", desc: "Brand guidelines", proj: "Brand identity", client: "Marlowe Coffee Co.", who: "M", time: "13:00–16:30", dur: "3:30", amt: "$420.00", st: "Unbilled", tone: "amber" },
        { k: "wire", desc: "Booking flow wireframes", proj: "Website refresh", client: "Orchard Veterinary", who: "J", time: "09:00–11:50", dur: "2:50", amt: "$340.00", st: "Billed", tone: "gray" },
        { k: "port", desc: "Portfolio update", proj: "Studio admin", client: "Northstar Studio", who: "P", time: "10:00–11:00", dur: "1:00", amt: "—", st: "Non-billable", tone: "gray" },
      ],
    },
    {
      label: "Tuesday, Oct 20", total: "6h 40m", money: "$800.00",
      rows: [
        { k: "shoot", desc: "Shot list for photo shoot", proj: "Menu relaunch", client: "Juniper Bakery", who: "J", time: "14:00–16:40", dur: "2:40", amt: "$320.00", st: "Billed", tone: "gray" },
        { k: "sketch", desc: "Logo sketches", proj: "Brand identity", client: "Marlowe Coffee Co.", who: "M", time: "09:00–13:00", dur: "4:00", amt: "$480.00", st: "Billed", tone: "gray" },
      ],
    },
  ];
});
</script>

<style>
/* Design tokens: shadcn Neutral (the app's default scheme) as in InvoicesAppWindow; status tones from
   lib/view/widgets/core/app_tone.dart. */
.ttw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: rgb(10 10 10 / 0.72); --ink3: rgb(10 10 10 / 0.55); --quiet: rgb(10 10 10 / 0.35); --selected: rgb(10 10 10 / 0.06);
  --pill-set: rgb(10 10 10 / 0.055); --pill-edge: rgb(10 10 10 / 0.12); --day: rgb(10 10 10 / 0.035);
  --gray-t: #5e5e5e; --gray-f: rgb(0 0 0 / 0.067); --green-t: #2d7a4e; --green-f: #e6f4ea;
  --amber-t: #946000; --amber-f: #fdf4c3; --live: #2fa564;
  --strip: #262626; --strip-ink: #fafafa;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .ttw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: rgb(250 250 250 / 0.72); --ink3: rgb(250 250 250 / 0.55); --quiet: rgb(250 250 250 / 0.35); --selected: rgb(250 250 250 / 0.1);
  --pill-set: rgb(250 250 250 / 0.08); --pill-edge: rgb(250 250 250 / 0.12); --day: rgb(250 250 250 / 0.05);
  --gray-t: #bdbdbd; --gray-f: rgb(255 255 255 / 0.12); --green-t: #6ed39a; --green-f: rgb(63 178 110 / 0.18);
  --amber-t: #f1c95e; --amber-f: rgb(224 170 36 / 0.17); --live: #5bd08e;
  --strip: #2e2e2e; --strip-ink: #fafafa;
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.ttw { min-height: 640px; }
@media (max-width: 1128px) { .ttw { min-height: 560px; } }
@media (max-width: 648px) { .ttw { min-height: 0; aspect-ratio: 390 / 700; } }
.ttw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.ttw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.ttw .ic.sm { width: 16px; height: 16px; }
.ttw .ic.xs { width: 14px; height: 14px; }
.ttw-grow { flex: 1; }
.ttw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ttw .muted { color: var(--ink3); }

/* ---------- desktop shell (as InvoicesAppWindow) ---------- */
.ttw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 640px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
  -webkit-mask-image: linear-gradient(#000 90%, transparent);
  mask-image: linear-gradient(#000 90%, transparent);
}
.dark .ttw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .ttw-phone { border-color: #3a3a3a; }
.ttw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.ttw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.ttw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.ttw-ws .ic { margin-left: auto; color: var(--ink3); }
.ttw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .ttw-logo { background: #fafafa; color: #171717; }
.ttw-nav { flex: 1; min-height: 0; overflow: hidden; }
.ttw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.ttw-ni.top { color: var(--ink); font-weight: 600; }
.ttw-ni.child { padding-left: 38px; }
.ttw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 600; }
.ttw-ni .ttw-chev { margin-left: auto; color: var(--ink3); }
.ttw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.ttw-side-foot .ttw-ni { height: 30px; }
.ttw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.ttw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.ttw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.ttw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.ttw-icbtn { position: relative; display: grid; flex: none; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.ttw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.ttw-sheet { display: flex; flex-direction: column; min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* ---------- Time Tracking (time_entries_screen.dart, desktop) ---------- */
.ttw-top { display: flex; flex: none; align-items: center; gap: 0; height: 56px; border-bottom: 1px solid rgb(from var(--ink) r g b / 0.07); padding: 0 20px; }
.ttw-title { display: flex; flex: none; align-items: center; gap: 8px; margin-right: 28px; font-size: 14px; font-weight: 600; white-space: nowrap; }
.ttw-tabs { display: flex; align-self: stretch; gap: 18px; min-width: 0; overflow: hidden; }
.ttw-tabs span { display: flex; align-items: center; gap: 6px; border-bottom: 2px solid transparent; color: rgb(from var(--ink) r g b / 0.6); white-space: nowrap; }
.ttw-tabs .on { border-bottom-color: var(--ink); color: var(--ink); font-weight: 600; }
.ttw-tabs b { display: grid; place-items: center; min-width: 18px; height: 18px; border-radius: 4px; background: #3f58bf; padding: 0 5px; color: #fff; font-size: 11px; font-weight: 600; }
.ttw-top .ttw-btn { margin: 0 4px 0 12px; }
.ttw-btn { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 32px; border: 1px solid transparent; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; }
.ttw-btn.sec { border-color: var(--card-edge); background: var(--page); color: var(--ink); }
.ttw-body { min-height: 0; padding: 16px 20px 0; }
.ttw-timer { display: flex; align-items: center; gap: 6px; height: 52px; border: 1px solid rgb(from var(--ink) r g b / 0.08); border-radius: 10px; background: var(--page); padding: 0 8px 0 16px; box-shadow: 0 1px 2px rgb(0 0 0 / 0.04); }
.dark .ttw-timer { border-color: rgb(250 250 250 / 0.12); }
.ttw-dot { position: relative; display: grid; flex: none; place-items: center; width: 16px; height: 16px; margin-right: 4px; border-radius: 50%; background: var(--pill-set); }
.ttw-dot i { width: 8px; height: 8px; border-radius: 50%; background: var(--quiet); }
.ttw-dot.live { background: rgb(from var(--live) r g b / 0.16); }
.ttw-dot.live i { background: var(--live); animation: ttw-beat 1.6s ease-in-out infinite; }
.ttw-desc { flex: 1; min-width: 0; overflow: hidden; margin-right: 4px; font-size: 14px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.ttw-desc.hint, .hint { color: var(--quiet); font-weight: 500; }
.ttw-pill { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 28px; max-width: 300px; overflow: hidden; border: 1px solid var(--pill-edge); border-radius: 6px; padding: 0 10px; color: var(--ink2); font-weight: 500; white-space: nowrap; }
.ttw-pill.set { border-color: transparent; background: var(--pill-set); color: var(--ink); }
.ttw-pill.bill { border-color: rgb(from var(--green-t) r g b / 0.28); background: var(--green-f); color: var(--green-t); }
.ttw-pill em { overflow: hidden; color: var(--ink3); font-style: normal; text-overflow: ellipsis; }
.ttw-pill .ic { opacity: 0.8; }
.ttw-elapsed { margin: 0 4px 0 6px; font-size: 20px; font-weight: 600; letter-spacing: -0.01em; }
.ttw-sq { display: grid; flex: none; place-items: center; width: 34px; height: 34px; border: 1px solid var(--pill-edge); border-radius: 8px; color: var(--ink); }
.ttw-sq.fill { border-color: transparent; background: var(--primary); color: var(--on-primary); }
.ttw-av { display: inline-grid; flex: none; place-items: center; width: 22px; height: 22px; border-radius: 50%; background: #dbe4ff; color: #3f58bf; font-size: 11px; font-weight: 600; }
.ttw-av.sm { width: 18px; height: 18px; font-size: 10px; }
.dark .ttw-av { background: rgb(91 127 234 / 0.25); color: #93aef9; }
.ttw-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 16px; border-top: 1px solid var(--line2); border-bottom: 1px solid var(--line2); }
.ttw-m { display: grid; align-content: start; gap: 2px; min-width: 0; border-left: 1px solid var(--line2); padding: 12px 16px 14px; }
.ttw-m:first-child { border-left: 0; padding-left: 0; }
.ttw-m small { color: var(--ink2); font-size: 12px; font-weight: 500; }
.ttw-m b { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.25; transform-origin: left center; }
.ttw-m span { overflow: hidden; color: var(--ink3); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.ttw-filters { display: flex; align-items: center; gap: 6px; height: 28px; margin: 12px 0 8px; }
.ttw-chip { display: inline-flex; flex: none; align-items: center; gap: 5px; height: 26px; border: 1px dashed var(--pill-edge); border-radius: 4px; padding: 0 8px; color: var(--ink2); font-size: 12px; font-weight: 500; white-space: nowrap; }
.ttw-chip.on { border-style: solid; border-color: var(--card-edge); background: var(--pill-set); color: var(--ink); }
.ttw-chip b { font-weight: 600; }
.ttw-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.ttw-table .w-ck { width: 40px; } .ttw-table .w-desc { width: 26%; } .ttw-table .w-proj { width: 23%; } .ttw-table .w-mem { width: 72px; }
.ttw-table .w-time { width: 104px; } .ttw-table .w-dur { width: 78px; } .ttw-table .w-amt { width: 96px; } .ttw-table .w-st { width: 132px; } .ttw-table .w-more { width: 40px; }
.ttw-table th { height: 34px; border-bottom: 1px solid var(--line2); color: rgb(from var(--ink) r g b / 0.52); font-weight: 500; text-align: left; white-space: nowrap; }
.ttw-table td { height: 40px; border-bottom: 1px solid var(--line2); white-space: nowrap; }
.ttw-table .r { padding-right: 4px; text-align: right; }
.ttw-table .c-st { padding-left: 16px; }
.ttw-table .strong { font-weight: 600; }
.ttw-table .quiet { color: var(--quiet); }
.ttw-table tr.ttw-day td { height: 32px; background: var(--day); font-size: 12px; font-weight: 600; }
.ttw-table tr.ttw-day td.r { color: var(--ink2); }
.ttw-table tr.ttw-day td.r:last-of-type { font-weight: 400; }
.ttw-table tr.is-focus td { animation: ttw-wash 2.2s ease-out both; }
.ttw-ck { display: block; width: 16px; height: 16px; margin-left: 4px; border: 1.5px solid var(--quiet); border-radius: 3px; }
.ttw-proj { display: grid; min-width: 0; padding-right: 10px; line-height: 1.2; }
.ttw-proj b { font-weight: 400; }
.ttw-proj span { color: var(--ink3); font-size: 12px; }
.ttw-tag { display: inline-flex; align-items: center; gap: 5px; height: 20px; border-radius: 4px; background: var(--gray-f); padding: 0 8px; color: var(--gray-t); font-style: normal; font-weight: 500; white-space: nowrap; }
.ttw-tag > i { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.ttw-tag.green { background: var(--green-f); color: var(--green-t); }
.ttw-tag.amber { background: var(--amber-f); color: var(--amber-t); }
.ttw-more { color: var(--ink3); text-align: center; }
.ttw-pop { animation: ttw-pop 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes ttw-pop { from { transform: scale(0.92); opacity: 0.35; } to { transform: none; opacity: 1; } }
@keyframes ttw-wash { from, 30% { background: var(--green-f); } to { background: transparent; } }
@keyframes ttw-in { from { opacity: 0; } }
@keyframes ttw-beat { 50% { opacity: 0.45; } }

/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it; the table drops the columns
   it would otherwise scroll sideways to. */
@container (max-width: 1080px) {
  .ttw-desk { grid-template-columns: minmax(0, 1fr); height: 560px; }
  .ttw-side, .ttw-hsearch { display: none; }
  .ttw-head { padding-left: 14px; }
  .ttw-comp { display: none; }
  .ttw-table .w-mem, .ttw-table .w-time { width: 0; }
  .ttw-table .c-mem, .ttw-table .c-time { overflow: hidden; padding: 0; font-size: 0; }
  .ttw-table .w-st { width: 118px; }
  .ttw-timer .ttw-pill.set em { display: none; }
}
@container (max-width: 860px) {
  .ttw-m { padding: 10px 12px 12px; }
  .ttw-m b { font-size: 20px; }
  .ttw-filters .ttw-chip:nth-child(n+4):not(:last-child) { display: none; }
  .ttw-tabs span:nth-child(n+4) { display: none; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.ttw-phone { display: none; }
@container (max-width: 600px) {
  .ttw-desk { display: none; }
  .ttw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--page);
    font-size: calc(var(--t) * 13);
  }
  .ttw-phone .ic { width: calc(var(--t) * 15); height: calc(var(--t) * 15); }
  /* Running strip (52pt) and the idle timer bar share the same reserved block, so the list below never moves. */
  .ttp-strip { position: absolute; z-index: 2; top: 0; right: 0; left: 0; display: flex; align-items: center; gap: calc(var(--u) * 10); height: calc(var(--u) * 58); background: var(--strip); padding: 0 calc(var(--u) * 12) 0 calc(var(--u) * 16); color: var(--strip-ink); transition: opacity 0.35s ease, transform 0.35s ease; }
  .ttp-strip.off { opacity: 0; transform: translateY(-100%); }
  .ttp-sdot { flex: none; width: calc(var(--u) * 9); height: calc(var(--u) * 9); border-radius: 50%; background: #5bd08e; animation: ttw-beat 1.6s ease-in-out infinite; }
  .ttp-stxt { display: grid; flex: 1; min-width: 0; line-height: 1.2; }
  .ttp-stxt b { font-size: calc(var(--t) * 13); font-weight: 600; }
  .ttp-stxt small { opacity: 0.7; font-size: calc(var(--t) * 11); }
  .ttp-sel { font-size: calc(var(--t) * 17); font-weight: 600; }
  .ttp-ssq { display: grid; flex: none; place-items: center; width: calc(var(--u) * 36); height: calc(var(--u) * 36); border: 1px solid rgb(250 250 250 / 0.22); border-radius: calc(var(--u) * 8); }
  .ttp-ssq.fill { border-color: transparent; background: #fafafa; color: #171717; }
  .ttp-tabs { position: relative; display: flex; align-items: stretch; gap: calc(var(--u) * 20); height: calc(var(--u) * 44); overflow: hidden; transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1); border-bottom: 1px solid var(--line2); padding: 0 calc(var(--u) * 44) 0 calc(var(--u) * 16); font-size: calc(var(--t) * 14); transition: margin-top 0.35s ease; }
  .ttw--running .ttp-tabs { transform: translateY(calc(var(--u) * 58)); }
  .ttp-tabs > span { display: flex; align-items: center; border-bottom: 2px solid transparent; color: var(--ink3); white-space: nowrap; }
  .ttp-tabs .on { border-bottom-color: var(--ink); color: var(--ink); font-weight: 600; }
  .ttp-tmore { position: absolute; top: 0; right: 0; bottom: 0; width: calc(var(--u) * 44); justify-content: center; border: 0 !important; background: var(--page); color: var(--ink2) !important; }
  .ttp-body { display: grid; gap: calc(var(--u) * 10); padding: calc(var(--u) * 12) calc(var(--u) * 16) 0; }
  .ttp-timer { display: grid; gap: calc(var(--u) * 8); border: 1px solid var(--pill-edge); border-radius: calc(var(--u) * 10); padding: calc(var(--u) * 10) calc(var(--u) * 10) calc(var(--u) * 10) calc(var(--u) * 14); animation: ttw-in 0.4s ease both; }
  /* Running: the card leaves (its slot stays, so nothing below changes size) and the rest of the page
     slides up under the banner by transform only: 58 banner - (104 card + 10 gap) = -56. */
  .ttw--running .ttp-timer { visibility: hidden; opacity: 0; }
  .ttp-body > * { transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s; }
  .ttw--running .ttp-body > :not(.ttp-timer) { transform: translateY(calc(var(--u) * -56)); }
  .ttp-trow { display: flex; align-items: center; gap: calc(var(--u) * 8); }
  .ttp-trow .hint { flex: 1; font-size: calc(var(--t) * 14); }
  .ttw-phone .ttw-btn { height: calc(var(--u) * 40); gap: calc(var(--u) * 4); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 14); }
  .ttp-pills { display: flex; flex-wrap: wrap; gap: calc(var(--u) * 6); }
  .ttw-phone .ttw-pill { height: calc(var(--u) * 34); gap: calc(var(--u) * 5); padding: 0 calc(var(--u) * 9); font-size: calc(var(--t) * 12); }
  .ttw-phone .ttw-av.sm { width: calc(var(--u) * 20); height: calc(var(--u) * 20); font-size: calc(var(--t) * 9); }
  .ttp-metrics { display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid var(--line2); border-bottom: 1px solid var(--line2); }
  .ttp-metrics .ttw-m { padding: calc(var(--u) * 10) calc(var(--u) * 12) calc(var(--u) * 10); }
  .ttp-metrics .ttw-m:first-child { padding-left: 0; }
  .ttp-metrics small, .ttp-metrics span { font-size: calc(var(--t) * 11); }
  .ttp-metrics b { font-size: calc(var(--t) * 18); }
  .ttp-filters { display: flex; gap: calc(var(--u) * 6); overflow: hidden; }
  .ttw-phone .ttw-chip { height: calc(var(--t) * 24); gap: calc(var(--u) * 4); font-size: calc(var(--t) * 11); }
  .ttw-phone .ttw-chip .ic { width: calc(var(--t) * 12); height: calc(var(--t) * 12); }
  .ttp-day { display: flex; align-items: baseline; justify-content: space-between; margin: calc(var(--u) * 4) calc(var(--u) * -16) calc(var(--u) * -6); padding: calc(var(--u) * 6) calc(var(--u) * 16) calc(var(--u) * 4); }
  .ttp-day b { font-weight: 600; }
  .ttp-day span { color: rgb(from var(--ink) r g b / 0.65); font-size: calc(var(--t) * 12); }
  .ttp-row { display: flex; align-items: center; gap: calc(var(--u) * 10); min-height: calc(var(--u) * 44); margin: 0 calc(var(--u) * -16); border-bottom: 1px solid var(--line2); padding: calc(var(--u) * 6) calc(var(--u) * 16); }
  .ttp-row.is-focus { animation: ttw-wash 2.2s ease-out both; }
  .ttp-l { display: grid; flex: 1; min-width: 0; gap: calc(var(--u) * 1); }
  .ttp-l b { font-size: calc(var(--t) * 14); font-weight: 500; }
  .ttp-l span { color: var(--ink3); font-size: calc(var(--t) * 12); }
  .ttp-r { display: grid; flex: none; justify-items: end; gap: calc(var(--u) * 3); }
  .ttp-r b { font-size: calc(var(--t) * 15); font-weight: 600; }
  .ttw-phone .ttw-tag { height: calc(var(--t) * 19); gap: calc(var(--u) * 4); padding: 0 calc(var(--u) * 6); font-size: calc(var(--t) * 11); }
  .ttw-phone .ttw-tag .ic { width: calc(var(--t) * 11); height: calc(var(--t) * 11); }
  .ttp-fab { position: absolute; z-index: 3; right: calc(var(--u) * 16); bottom: calc(var(--u) * 82); display: inline-flex; align-items: center; gap: calc(var(--u) * 6); height: calc(var(--u) * 48); border-radius: 8px; background: var(--primary); padding: 0 calc(var(--u) * 18); color: var(--on-primary); font-size: calc(var(--t) * 14); font-weight: 600; box-shadow: 0 4px 12px rgb(0 0 0 / 0.14); }
  .ttp-nav { position: absolute; z-index: 3; right: calc(var(--u) * 12); bottom: calc(var(--u) * 14); left: calc(var(--u) * 12); display: flex; align-items: center; gap: calc(var(--u) * 4); border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); padding: calc(var(--u) * 6); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12), 0 -14px 18px -6px var(--page); }
  .ttp-nav::after { content: ""; position: absolute; right: calc(var(--u) * -13); bottom: calc(var(--u) * -15); left: calc(var(--u) * -13); height: calc(var(--u) * 14); background: var(--page); }
  .ttp-nav > span:not(.ttp-navmore) { display: grid; flex: 1; justify-items: center; gap: calc(var(--u) * 2); min-width: 0; color: var(--ink3); font-size: calc(var(--u) * 10.5); white-space: nowrap; }
  .ttp-nav .on { color: var(--ink); font-weight: 600; }
  .ttp-nav small { max-width: 100%; overflow: hidden; font-size: inherit; text-overflow: ellipsis; }
  .ttp-nav .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .ttp-navmore { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border-radius: 8px; background: var(--primary); color: var(--on-primary); }
}
@media (prefers-reduced-motion: reduce) { .ttw * { animation: none !important; transition: none !important; } }
</style>
